/* eslint-disable no-console */
// Inpired by: https://dev.to/aduranil/how-to-use-websockets-with-redux-a-step-by-step-guide-to-writing-understanding-connecting-socket-middleware-to-your-project-km3
import { datadogLogs } from '@datadog/browser-logs';
import dayjs from 'dayjs';

import { getSocketURL, SocketType } from 'services/socketHelpers';
import {
    setUserSupportConversation,
    setUserSupportReferral,
    SocketMessageTypes,
} from 'services/userSupportHelpers';
import { receiveNewNotifications } from 'store/notification/notificationThunks';
import {
    setConnectionLoading,
    setSocketConnected,
    updateConversationParticipation,
    updateConversations,
    updateMessages,
    updateUserParticipation,
} from 'store/userSupport/userSupportSlice';

const wsConnected = (socketType, host) => ({ type: 'WS_CONNECTED', socketType, host });
const wsDisconnected = (socketType, host) => ({ type: 'WS_DISCONNECTED', socketType, host });
const wsReconnect = (socketType, host) => ({ type: 'WS_RECONNECT', socketType, host });

const sockets = {
    [SocketType.USER_SUPPORT]: null,
    [SocketType.NOTIFICATION]: null,
};

const socketMap = {
    [SocketType.NOTIFICATION]: {
        template: {
            store: null,
            socketType: SocketType.NOTIFICATION,
            socketUrl: null,
            messageMap: {
                STATUS: (_store, message) =>
                    console.log(`Notification WebSocket Status: ${message.message}`),
                NOTIFICATION: (store, message) =>
                    store.dispatch(receiveNewNotifications(message.payload)),
            },
        },
        actions: {
            WS_CONNECT: (store, _action, template) => ({
                ...template,
                store,
                socketUrl: getSocketURL(SocketType.NOTIFICATION),
            }),
        },
    },
    [SocketType.USER_SUPPORT]: {
        template: {
            store: null,
            transactionId: null,
            socketType: SocketType.USER_SUPPORT,
            socketUrl: null,
            messageMap: {
                [SocketMessageTypes.STATUS]: (_store, message) =>
                    console.log(`User Support WebSocket Status: ${message.message}`),
                [SocketMessageTypes.MESSAGES]: (store, message) => {
                    store.dispatch(updateMessages(message.payload));
                    const { canLogWebsocketPerformance } = store.getState().userSupport;
                    if (canLogWebsocketPerformance) {
                        datadogLogs.logger.info(
                            `socket set messages for convo ${message.payload.conversation_id}`,
                            {
                                conversation_info: message.payload,
                                timestamp_in_seconds: (dayjs().valueOf() / 1000).toString(),
                            },
                        );
                    }
                },
                [SocketMessageTypes.CONVERSATIONS]: (store, message) => {
                    store.dispatch(updateConversations(message.payload));
                    const { canLogWebsocketPerformance } = store.getState().userSupport;
                    if (canLogWebsocketPerformance) {
                        if (message.payload.length > 1) {
                            datadogLogs.logger.info('socket set conversations', {
                                conversations: message.payload,
                                timestamp_in_seconds: (dayjs().valueOf() / 1000).toString(),
                            });
                        } else if (message.payload.length === 1) {
                            datadogLogs.logger.info('socket set current conversation', {
                                conversation: message.payload[0],
                                timestamp_in_seconds: (dayjs().valueOf() / 1000).toString(),
                            });
                        }
                    }
                },
                [SocketMessageTypes.USER_PARTICIPATION]: (store, message) =>
                    store.dispatch(updateUserParticipation(message.payload)),
                [SocketMessageTypes.CONVERSATION_PARTICIPATION]: (store, message) =>
                    store.dispatch(updateConversationParticipation(message.payload)),
            },
        },
        actions: {
            WS_CONNECT: (store, _action, template) => ({
                ...template,
                store,
                socketUrl: getSocketURL(SocketType.USER_SUPPORT),
                onOpenAction: () => {
                    const {
                        userSupport: { currentReferral, currentConversation },
                    } = store.getState();
                    if (currentReferral) {
                        store.dispatch(setUserSupportReferral(currentReferral));
                        if (currentConversation) {
                            store.dispatch(setUserSupportConversation(currentConversation));
                        }
                    }
                    store.dispatch(setSocketConnected({ connected: true }));
                    store.dispatch(setConnectionLoading({ loading: false }));
                },
                onCloseAction: () => {
                    store.dispatch(setSocketConnected({ connected: false }));
                    store.dispatch(setConnectionLoading({ loading: false }));
                },
                onReconnectAttempt: () => {
                    store.dispatch(setConnectionLoading({ loading: true }));
                },
                checkIfDisconnected: () => {
                    const { userSupport } = store.getState();
                    const { socketConnected, connectionLoading } = userSupport;
                    return !socketConnected && !connectionLoading;
                },
            }),
            WS_SEND: (_store, action) => ({
                type: action.messageType,
                ...action.message,
            }),
        },
    },
};

const openSocket = ({
    store,
    socketType,
    socketUrl,
    messageMap,
    onOpenAction,
    onCloseAction,
    onReconnectAttempt,
    checkIfDisconnected,
}) => {
    let socket = null;
    let timerId = 0;
    let pingSendsCounter = 0; // "unacknowledged" ping sends

    const cancelKeepAlive = () => {
        pingSendsCounter = 0;
        if (timerId) {
            clearTimeout(timerId);
        }
    };

    const keepAlive = () => {
        const timeout = 20000;
        if (socket.readyState === socket.OPEN) {
            socket.send(JSON.stringify({ type: 'KEEP-ALIVE' }));
        }
        pingSendsCounter += 1;
        if (pingSendsCounter >= 3) {
            console.warn(
                `No Server Response in ${
                    (timeout * pingSendsCounter) / 1000
                } seconds - Reconnecting ${socketType}`,
            );
            onReconnectAttempt?.();
            store.dispatch(wsReconnect(socketType));
        } else if (checkIfDisconnected?.() && pingSendsCounter >= 1) {
            console.warn(`Server unexpectedly disconnected - Reconnecting ${socketType}`);
            onReconnectAttempt?.();
            store.dispatch(wsReconnect(socketType));
        } else {
            timerId = setTimeout(() => keepAlive(), timeout);
        }
    };

    const isOpen = () => socket != null;

    const close = () => {
        socket.close();
    };

    const send = (message) => {
        if (socket.readyState === socket.OPEN) {
            socket.send(message);
        } else {
            setTimeout(() => socket.send(message), 2000);
        }
    };

    // make sure any old sockets aren't doing any work
    cancelKeepAlive();

    // connect to the remote host
    socket = new WebSocket(`${socketUrl}/ws`);

    // websocket handlers
    socket.onopen = (event) => {
        // authenticate
        const { canLogWebsocketPerformance } = store.getState().userSupport;
        if (canLogWebsocketPerformance) {
            datadogLogs.logger.info('socket opened', {
                timestamp_in_seconds: (dayjs().valueOf() / 1000).toString(),
            });
        }
        const token = store.getState().auth.accessToken;
        const authBody = JSON.stringify({ type: 'AUTH', token });
        socket.send(authBody);
        if (canLogWebsocketPerformance) {
            datadogLogs.logger.info('socket authenticated', {
                timestamp_in_seconds: (dayjs().valueOf() / 1000).toString(),
            });
        }

        console.log('websocket open', event);
        store.dispatch(wsConnected(socketType, event));

        keepAlive();

        onOpenAction?.();
    };

    socket.onclose = () => {
        console.log(`${socketType} was closed.`);
        onCloseAction?.();
        store.dispatch(wsDisconnected(socketType));
    };

    socket.onmessage = (event) => {
        // reset ping sends counter
        pingSendsCounter = 0;
        const message = JSON.parse(event.data);
        const { canLogWebsocketPerformance } = store.getState().userSupport;
        if (message && messageMap[message.type]) {
            if (message.type !== SocketMessageTypes.STATUS && canLogWebsocketPerformance) {
                datadogLogs.logger.info(`socket received ${message.type} message`, {
                    timestamp_in_seconds: (dayjs().valueOf() / 1000).toString(),
                });
            }
            messageMap[message.type](store, message);
        } else {
            console.log('Unknown message type', message);
        }
    };

    return {
        isOpen,
        close,
        send,
    };
};

const socketMiddleware = () => {
    const isSocketOpen = (socketType) => sockets[socketType]?.isOpen();

    const openMappedSocket = (store, action) => {
        sockets[action.socketType] = openSocket(
            socketMap[action.socketType].actions.WS_CONNECT(
                store,
                action,
                socketMap[action.socketType].template,
            ),
        );
    };

    const sendOnMappedSocket = (store, action) => {
        console.log(
            `sending a message ${action.messageType} to socket ${action.socketType}`,
            action.message,
        );
        const { canLogWebsocketPerformance } = store.getState().userSupport;
        if (canLogWebsocketPerformance) {
            datadogLogs.logger.info(
                `${action.messageType} message sent to socket ${action.socketType}`,
                {
                    message_info: action.message,
                    timestamp_in_seconds: (dayjs().valueOf() / 1000).toString(),
                },
            );
        }
        const socketWSSend = socketMap[action.socketType].actions.WS_SEND;
        sockets[action.socketType].send(
            JSON.stringify(
                socketWSSend
                    ? socketWSSend(store, action)
                    : { type: action.messageType, message: action.message },
            ),
        );
    };

    const actionMap = {
        WS_CONNECT: (store, action) => {
            if (isSocketOpen(action.socketType)) {
                sockets[action.socketType].close();
            }

            openMappedSocket(store, action);
        },
        WS_ENSURE_CONNECTED: (store, action) => {
            if (!isSocketOpen(action.socketType)) {
                openMappedSocket(store, action);
            }
        },
        WS_RECONNECT: (store, action) => {
            openMappedSocket(store, action);
        },
        WS_DISCONNECT: (_store, action) => {
            if (isSocketOpen(action.socketType)) {
                sockets[action.socketType].close();
            }
        },
        WS_SEND: (store, action) => {
            if (!isSocketOpen(action.socketType)) {
                setTimeout(() => sendOnMappedSocket(store, action), 2000);
            } else {
                sendOnMappedSocket(store, action);
            }
        },
    };

    // the middleware part of this function
    return (store) => (next) => (action) => {
        if (action && actionMap[action.type]) {
            return actionMap[action.type](store, action);
        }
        return next(action);
    };
};

export default socketMiddleware();
